import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Sparkles, Plus, Check, X, Share2, MessageCircle, Pencil } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";

export default function AuthorDashboard() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const [{ data: cs }, { data: as }] = await Promise.all([
        api.get("/campaigns/mine"),
        api.get("/applications/received"),
      ]);
      setCampaigns(cs); setApps(as);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const decide = async (id, decision) => {
    try {
      await api.post(`/applications/${id}/decision`, { decision });
      toast.success(`Application ${decision}`);
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this campaign? All applications will be removed.")) return;
    try {
      await api.delete(`/campaigns/${id}`);
      toast.success("Campaign deleted");
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleAutoAccept = async (v) => {
    try {
      const { data } = await api.put("/profile/author", { auto_accept_enabled: v });
      setUser(data);
      toast.success(`Auto-accept ${v ? "enabled" : "disabled"}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const messageReader = async (readerId) => {
    try {
      const { data } = await api.post("/conversations", { other_user_id: readerId });
      nav(`/messages?c=${data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2">Author Dashboard</div>
          <h1 className="font-serif text-4xl tracking-tight">Studio · {user?.display_name}</h1>
          <p className="text-slate-600 mt-1 text-sm">Manage your campaigns, review reader applications and pen names.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="btn-press"
            onClick={async () => {
              const url = window.location.origin + `/authors/${user.id}`;
              try {
                if (navigator.share) await navigator.share({ title: user.display_name, url });
                else { await navigator.clipboard.writeText(url); toast.success("Public page link copied"); }
              } catch { /* cancelled */ }
            }}
            data-testid="author-share-public-btn"
          >
            <Share2 size={14} className="mr-2" /> Share public page
          </Button>
          <Link to={`/authors/${user?.id}`}><Button variant="outline" className="btn-press" data-testid="author-view-public-btn">View public page</Button></Link>
          <Link to="/profile"><Button variant="outline" className="btn-press" data-testid="author-profile-btn">Edit profile</Button></Link>
          <Link to="/author/new"><Button className="btn-press btn-rainbow" data-testid="author-new-campaign-btn"><Plus size={16} className="mr-1" />New campaign</Button></Link>
        </div>
      </div>

      <div className="paper-card p-5 mb-8 flex items-center justify-between gap-4" data-testid="author-auto-accept-card">
        <div>
          <div className="flex items-center gap-2 mb-1"><Sparkles size={16} className="text-indigo-800" /><span className="font-serif text-lg">Auto-accept trusted readers</span></div>
          <p className="text-sm text-slate-600 max-w-xl">When on, readers who have opted-in to your auto-accept agreement are approved instantly for your campaigns.</p>
        </div>
        <Switch checked={!!user?.auto_accept_enabled} onCheckedChange={toggleAutoAccept} data-testid="author-auto-accept-switch" />
      </div>

      <Tabs defaultValue="campaigns" className="w-full">
        <TabsList data-testid="author-tabs">
          <TabsTrigger value="campaigns" data-testid="tab-campaigns">Campaigns ({campaigns.length})</TabsTrigger>
          <TabsTrigger value="applications" data-testid="tab-applications">Applications ({apps.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="campaigns" className="mt-6">
          {loading ? <div className="text-slate-500">Loading…</div> : campaigns.length === 0 ? (
            <div className="paper-card p-10 text-center">
              <div className="font-serif text-2xl mb-2">No campaigns yet.</div>
              <p className="text-sm text-slate-600 mb-4">Post your first ARC campaign in under a minute.</p>
              <Link to="/author/new"><Button className="btn-press bg-[color:var(--oxford)] hover:bg-[color:var(--oxford-hover)] text-white">Create campaign</Button></Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {campaigns.map((c) => (
                <div key={c.id} className="paper-card p-4 flex gap-4" data-testid={`author-campaign-${c.id}`}>
                  <img src={c.cover_url || "https://images.unsplash.com/photo-1620233977581-509e4c6b0636?crop=entropy&cs=srgb&fm=jpg&q=85&w=400"} alt="" className="w-20 h-28 rounded object-cover" />
                  <div className="flex-1 min-w-0">
                    <Link to={`/campaigns/${c.id}`} className="font-serif text-xl hover:underline block truncate">{c.title}</Link>
                    <div className="text-sm text-slate-600">Pen: {c.pen_name} · {c.genre}</div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Badge variant="outline" className="capitalize">{c.arc_format}</Badge>
                      <Badge variant="outline">{c.copies_available} copies</Badge>
                      <Badge variant="outline">{c.applications_count} applied</Badge>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 items-end self-start">
                    <Link to={`/author/campaign/${c.id}/edit`}>
                      <Button size="sm" variant="outline" className="btn-press" data-testid={`edit-campaign-${c.id}`}>
                        <Pencil size={13} className="mr-1" /> Edit
                      </Button>
                    </Link>
                    <button onClick={() => del(c.id)} className="text-slate-400 hover:text-rose-600 mt-1" data-testid={`delete-campaign-${c.id}`}><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="applications" className="mt-6">
          {apps.length === 0 ? (
            <div className="paper-card p-10 text-center text-slate-600">No applications yet. Once readers apply, they’ll show up here.</div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {apps.map((a) => (
                <div key={a.id} className="paper-card p-4 flex flex-col md:flex-row md:items-center gap-4" data-testid={`app-received-${a.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-500">For <span className="font-medium text-slate-800">{a.campaign?.title}</span></div>
                    <div className="font-serif text-lg">{a.reader?.display_name}</div>
                    <div className="text-xs flex flex-wrap gap-x-3 gap-y-1 mt-1" style={{ color: "var(--ink-muted)" }}>
                      <span>Format: <span className="capitalize">{a.preferred_format}</span></span>
                      {a.reader?.goodreads_url && <a className="underline" style={{ color: "#FFD400" }} href={a.reader.goodreads_url} target="_blank" rel="noreferrer">Goodreads</a>}
                      {a.reader?.storygraph_url && <a className="underline" style={{ color: "#FFD400" }} href={a.reader.storygraph_url} target="_blank" rel="noreferrer">StoryGraph</a>}
                      {a.reader?.amazon_reviewer_url && <a className="underline" style={{ color: "#FFD400" }} href={a.reader.amazon_reviewer_url} target="_blank" rel="noreferrer">Amazon</a>}
                      {a.reader?.substack_url && <a className="underline" style={{ color: "#FFD400" }} href={a.reader.substack_url} target="_blank" rel="noreferrer">Substack</a>}
                      {a.reader?.instagram_url && <a className="underline" style={{ color: "#00C7FF" }} href={a.reader.instagram_url} target="_blank" rel="noreferrer">Instagram</a>}
                      {a.reader?.tiktok_url && <a className="underline" style={{ color: "#00C7FF" }} href={a.reader.tiktok_url} target="_blank" rel="noreferrer">TikTok</a>}
                      {a.reader?.youtube_url && <a className="underline" style={{ color: "#00C7FF" }} href={a.reader.youtube_url} target="_blank" rel="noreferrer">YouTube</a>}
                      {a.reader?.twitter_url && <a className="underline" style={{ color: "#00C7FF" }} href={a.reader.twitter_url} target="_blank" rel="noreferrer">X</a>}
                      {a.reader?.facebook_url && <a className="underline" style={{ color: "#00C7FF" }} href={a.reader.facebook_url} target="_blank" rel="noreferrer">Facebook</a>}
                      {a.reader?.threads_url && <a className="underline" style={{ color: "#00C7FF" }} href={a.reader.threads_url} target="_blank" rel="noreferrer">Threads</a>}
                      {a.reader?.bluesky_url && <a className="underline" style={{ color: "#00C7FF" }} href={a.reader.bluesky_url} target="_blank" rel="noreferrer">Bluesky</a>}
                      {a.reader?.website_url && <a className="underline" style={{ color: "#E040FB" }} href={a.reader.website_url} target="_blank" rel="noreferrer">Website</a>}
                    </div>
                    {a.comment && <p className="text-sm text-slate-700 mt-2 italic border-l-2 border-amber-300 pl-3">"{a.comment}"</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`border capitalize ${a.status === "approved" ? "status-approved" : a.status === "rejected" ? "status-rejected" : "status-pending"}`}>
                      {a.status}
                    </Badge>
                    <Button size="sm" variant="outline" className="btn-press" onClick={() => messageReader(a.reader_id)} data-testid={`message-reader-${a.id}`}>
                      <MessageCircle size={14} className="mr-1" /> Message
                    </Button>
                    {a.status === "pending" && (
                      <>
                        <Button size="sm" onClick={() => decide(a.id, "approved")} className="btn-press bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-${a.id}`}><Check size={14} className="mr-1" />Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => decide(a.id, "rejected")} className="btn-press border-rose-200 text-rose-700 hover:bg-rose-50" data-testid={`reject-${a.id}`}><X size={14} className="mr-1" />Decline</Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
